import React, { useState } from 'react';
import { DeleteIcon, EditIcon, EyeIcon, PaymentIcon } from '../../components/Shared/TableActionIcons';
import { Dialog } from 'primereact/dialog';
import i18next from 'i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import CustomPagination from '../../components/Shared/CustomPagination';
import { useTranslation } from 'react-i18next';
import { formatTimestamp } from '../../components/Shared/SharedMethods';
import { deletePurchasedEntry } from '../../store/slices/PurchaseSlice';
import ReceivePayment from './ReceivePatment';
import TableLoading from '../../components/Shared/TableLoading';
import CustomTable from '../../components/Shared/CustomTable';
import ConfirmationModal from '../../components/Shared/ConfirmationModal';
import { FiTrash2 } from 'react-icons/fi';

export default function PurchasedEntryTable({ data, loading, onPageChange, onRefresh, selectedTransactionEntries, setSelectedTransactionEntries }) {
  const datebody = rowData => {
    return formatTimestamp(rowData.purchase_date);
  };

  const [createDialogVisible, setCreateDialogVisible] = useState(false);
  const [receivePaymentDialogVisible, setReceivePaymentDialogVisible] = useState(false);
  const [deleteDialogVisible, setDeleteDialogVisible] = useState(false);
  const [selectedData, setSelectedData] = useState(null);
  const { isLoading } = useSelector(state => state.purchase);
  const lang = i18next.language;
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const navigate = useNavigate();

  const handlePageChange = (newPage, newRowsPerPage) => {
    if (onPageChange) {
      onPageChange(newPage, newRowsPerPage);
    }
  };

  const handleDeleteClick = (rowData) => {
    setSelectedData(rowData);
    setDeleteDialogVisible(true);
  };

  const handleDeleteConfirm = async () => {
    const resultAction = await dispatch(deletePurchasedEntry(selectedData?.id));
    if (deletePurchasedEntry.fulfilled.match(resultAction)) {
      setDeleteDialogVisible(false);
      if (onRefresh) onRefresh();
    }
  };

  const handleEdit = (rowData) => {
    navigate('/manager/purchased_entry/edit', { state: { purchaseEntry: rowData } });
  };

  const templateBody = rowData => (
    <div className='flex gap-1'>
      <EyeIcon
        onClick={() => navigate('/manager/purchased_entry/view', { state: { purchaseEntry: rowData } })}
        title={t('view')}
      />

      <EditIcon
        onClick={() => handleEdit(rowData)}
        title={t('edit')}
      />

      <DeleteIcon
        onClick={() => handleDeleteClick(rowData)}
        title={t('delete')}
      />

      {rowData.status !== 'paid' && (
        <PaymentIcon
          onClick={() => {
            navigate('/manager/purchased_entry/receive-payment', { state: { selectedEntries: [rowData] } });
          }}
          title={t('Record Payment')}
        />
      )}
    </div>
  );
  const sequenceBodyTemplate = (rowData, options) => {
    return options.rowIndex + 1 + (data?.page - 1) * data?.per_page;
  };

  const statusBody = data => {
    if (data?.status === 'paid') {
      return <h1 className='bg-green-500 !w-[60px] p-1 text-center text-white rounded-md'>{t('Paid')}</h1>;
    } else {
      return <h1 className='bg-red-500 p-1 !w-[60px] text-center text-white rounded-md'>{t('Unpaid')}</h1>;
    }
  };

  const balanceBody = data => {
    return (
      <>
        {data?.total_amount} {t('SAR')}
      </>
    );
  };
  const vatBody = data => {
    return (
      <>
        {data?.total_vat_amount} {t('SAR')}
      </>
    );
  };

  const purchaseEntryColumns = [
    {
      body: templateBody,
      header: t('actions')
    },
    {
      body: sequenceBodyTemplate,
      header: t('no'),
      style: { width: '3rem' }
    },
    {
      field: 'purchase_date',
      header: t('purchase_date'),
      body: datebody
    },
    {
      field: 'entry_number',
      header: t('entry_number')
    },
    {
      field: 'vendor.name',
      header: t('Vendor Name')
    },
    {
      field: 'total_vat_amount',
      header: t('total_vat'),
      body: vatBody
    },
    {
      field: 'Net Amount',
      header: t('total_amount'),
      body: balanceBody
    },
    {
      field: 'notes',
      header: t('description')
    },
    {
      field: 'status',
      header: t('status'),
      body: statusBody
    },

  ];

  const entryDetailsColumns = [
    {
      field: lang === 'ar' ? 'item_name_ar' : 'item_name_en',
      header: t('item_name')
    },
    {
      field: 'item_price',
      header: t('item_price')
    },
    {
      field: 'quantity',
      header: t('quantity')
    },
    {
      field: 'subtotal',
      header: t('subtotal')
    },
    {
      field: 'vat_percent',
      header: t('vat_percent')
    },
    {
      field: 'vat_amount',
      header: t('vat_amount')
    },
    {
      field: 'total',
      header: t('total')
    }
  ];

  return (
    <>
      <CustomTable
        data={data?.data}
        isLoading={loading}
        loadingIcon={<TableLoading />}
        columns={purchaseEntryColumns}
        rows={data?.per_page}
      />

      {/* Detail Dialog */}
      <Dialog
        header={t('PurchaseDetail')}
        visible={createDialogVisible}
        onHide={() => {
          if (!createDialogVisible) return;
          setCreateDialogVisible(false);
        }}
        pt={{
          root: { className: 'w-full lg:w-10/12 mx-4 overflow-auto' },
          header: { className: '!p-2 !bg-darkBg !text-white' },
          content: { className: '!p-3' },
          closeButton: {
            className: 'hover:!bg-black/20 !outline-none !shadow-none'
          },
          closeButtonIcon: { className: '!text-white' }
        }}
      >
        <CustomTable data={selectedTransactionEntries} columns={entryDetailsColumns} />
      </Dialog>
      {data?.data?.length > 0 && (
        <CustomPagination
          currentPage={data?.page}
          rowsPerPage={data?.per_page}
          totalRecords={data?.total_pages}
          totalRecord={data?.total_record}
          onPageChange={handlePageChange}
        />
      )}

      {
        receivePaymentDialogVisible && (
          <ReceivePayment
            open={receivePaymentDialogVisible}
            setOpen={setReceivePaymentDialogVisible}
            selectedEntries={[]}
          />
        )
      }

      <ConfirmationModal
        message={`${t("do_you_really_want_to_delete_this_purchased_entry")} "${selectedData?.entry_number}"?`}
        header={t("delete_confirmation")}
        Icon={FiTrash2}
        isLoading={isLoading}
        visible={deleteDialogVisible}
        setVisible={setDeleteDialogVisible}
        onAccept={handleDeleteConfirm}
        acceptLabel={t("delete")}
        rejectLabel={t("cancel")}
      />

    </>
  );
}
